import { useMutation } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';
import { NSecSigner, type NostrEvent } from '@nostrify/nostrify';
import { VIDEO_KINDS } from '@/lib/videoTypes';
import {
  uploadWithProgress,
  uploadFileToAllServers,
  type BlossomSigner,
  type ServerProgress,
  type ServerUploadResult,
} from '@/lib/blossomUpload';
import { extractVideoThumbnail, probeVideoFile } from '@/lib/videoFile';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';

export type { ServerProgress, ServerUploadResult };

export interface VideoUploadData {
  title: string;
  description: string;
  file: File;
  thumbnailFile?: File;
  contentWarning?: string;
  hashtags: string[];
  /** Publish as an addressable event (kind 34235/34236) so it can be edited. */
  useAddressable: boolean;
  /**
   * Publish with a freshly generated one-time key instead of the logged-in
   * account. The nsec is returned so the user can keep or toss it.
   */
  anonymous: boolean;
  /** Called with per-server progress during the video upload step. */
  onServerProgress?: (updated: ServerProgress, all: ServerProgress[]) => void;
}

export interface VideoUploadResult {
  event: NostrEvent;
  videoUrl: string;
  /** Fallback URLs on the other Blossom servers. */
  fallbackUrls: string[];
  thumbnailUrl?: string;
  duration: number;
  videoServerResults: ServerUploadResult[];
  /** Present only for anonymous uploads — the one-time key. */
  anonymousKey?: { pubkey: string; nsec: string };
}

async function performUpload(
  data: VideoUploadData,
  signer: BlossomSigner,
  servers: string[],
  publish: (event: NostrEvent) => Promise<void>,
): Promise<Omit<VideoUploadResult, 'anonymousKey'>> {
  // Probe duration + dimensions from the file itself. Some containers (e.g.
  // mkv in some browsers) can't be probed — upload anyway without metadata.
  let metadata: { duration: number; width: number; height: number; isVertical: boolean } | null = null;
  try {
    metadata = await probeVideoFile(data.file);
  } catch (err) {
    console.warn('Could not probe video metadata:', err);
  }

  // Upload video file to all servers with progress tracking
  const descriptor = await uploadWithProgress(
    data.file,
    servers,
    signer,
    { onServerProgress: data.onServerProgress },
  );

  const videoUrl = descriptor.url;
  const videoHash = descriptor.sha256;
  const videoMime = descriptor.type;
  const videoServerResults = descriptor.serverResults;
  const fallbackUrls = videoServerResults
    .filter(r => r.success && r.url && r.url !== videoUrl)
    .map(r => r.url as string);

  // Extract or use provided thumbnail
  let thumbnailFile = data.thumbnailFile;
  if (!thumbnailFile) {
    try {
      thumbnailFile = await extractVideoThumbnail(data.file);
    } catch (error) {
      console.warn('Failed to extract thumbnail:', error);
    }
  }

  // Upload thumbnail to all servers (small file)
  let thumbnailUrl: string | undefined;
  if (thumbnailFile) {
    try {
      const thumbResult = await uploadFileToAllServers(thumbnailFile, servers, signer);
      thumbnailUrl = thumbResult.url;
    } catch (err) {
      console.warn('Thumbnail upload failed:', err);
    }
  }

  const now = Math.floor(Date.now() / 1000);
  const tags: string[][] = [];
  const isVertical = metadata?.isVertical ?? false;

  let kind: number;
  if (data.useAddressable) {
    kind = isVertical ? VIDEO_KINDS.addressable.vertical : VIDEO_KINDS.addressable.horizontal;
    tags.push(['d', crypto.randomUUID()]);
  } else {
    kind = isVertical ? VIDEO_KINDS.regular.vertical : VIDEO_KINDS.regular.horizontal;
  }

  const imetaParts = [
    'imeta',
    `url ${videoUrl}`,
  ];
  if (metadata) {
    imetaParts.push(`dim ${metadata.width}x${metadata.height}`);
    imetaParts.push(`duration ${metadata.duration.toFixed(3)}`);
  }
  if (videoMime) imetaParts.push(`m ${videoMime}`);
  if (videoHash) imetaParts.push(`x ${videoHash}`);
  if (thumbnailUrl) imetaParts.push(`image ${thumbnailUrl}`);
  for (const fallback of fallbackUrls) {
    imetaParts.push(`fallback ${fallback}`);
  }
  tags.push(imetaParts);

  tags.push(['title', data.title]);
  tags.push(['published_at', now.toString()]);
  if (data.contentWarning) tags.push(['content-warning', data.contentWarning]);
  for (const hashtag of data.hashtags) tags.push(['t', hashtag.toLowerCase()]);
  tags.push(['alt', `Video: ${data.title}`]);

  const event = await signer.signEvent({ kind, content: data.description, tags, created_at: now });

  await publish(event);

  return {
    event,
    videoUrl,
    fallbackUrls,
    thumbnailUrl,
    duration: metadata?.duration ?? 0,
    videoServerResults,
  };
}

/**
 * Upload a video. In anonymous mode a fresh one-time key is generated, used
 * for both Blossom auth and event signing, and returned to the caller so the
 * user can keep it (for future edits) or toss it.
 */
export function useVideoUpload() {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (data: VideoUploadData): Promise<VideoUploadResult> => {
      const servers = getEffectiveBlossomServers(
        config.blossomServerMetadata,
        config.useAppBlossomServers,
      );
      if (servers.length === 0) {
        throw new Error('No Blossom servers configured. Add at least one in Settings.');
      }

      if (data.anonymous || !user) {
        // One-time keypair: signs the Blossom auth tokens AND the video event
        const secretKey = generateSecretKey();
        const pubkey = getPublicKey(secretKey);
        const signer = new NSecSigner(secretKey);

        const result = await performUpload(data, signer, servers, async (event) => {
          await nostr.event(event);
        });

        return {
          ...result,
          anonymousKey: { pubkey, nsec: nip19.nsecEncode(secretKey) },
        };
      }

      // Authenticated upload with the user's own signer
      const result = await performUpload(data, user.signer, servers, async (event) => {
        await nostr.event(event);
      });
      return result;
    },
  });
}
